import { useMemo } from 'react';
import { getResourceTo } from 'react/utils/utils';

/**
 * Custom hook that calculates size configurations based on stop details
 * Follows Single Responsibility Principle - only handles size configuration logic
 *
 * @param {Object} stopDetail - The stop detail object containing size information
 * @returns {Array} Array of size configurations with formatted display values
 */
export const useSizeConfigs = (stopDetail) => useMemo(() => {
  if (!stopDetail) {
    return [];
  }

  const sizes = [];

  const getSizeAndTitle = (actual, planned) => {
    if (actual && planned) {
      return {
        size: `${actual}/${planned}`,
        title: getResourceTo('modal.stopdetails.actualplanned')
      };
    }

    return {
      size: planned,
      title: getResourceTo('modal.stopdetails.planned')
    };
  };

  if (stopDetail.size1AliasConfig && stopDetail.plannedSize1) {
    sizes.push({
      ...stopDetail.size1AliasConfig,
      sizeInfo: getSizeAndTitle(
        stopDetail.actualSize1,
        stopDetail.plannedSize1
      )
    });
  }

  if (stopDetail.size2AliasConfig && stopDetail.plannedSize2) {
    sizes.push({
      ...stopDetail.size2AliasConfig,
      sizeInfo: getSizeAndTitle(
        stopDetail.actualSize2,
        stopDetail.plannedSize2
      )
    });
  }

  if (stopDetail.size3AliasConfig && stopDetail.plannedSize3) {
    sizes.push({
      ...stopDetail.size3AliasConfig,
      sizeInfo: getSizeAndTitle(
        stopDetail.actualSize3,
        stopDetail.plannedSize3
      )
    });
  }

  return sizes;
}, [stopDetail]);

/**
 * Custom hook that handles modal container creation logic
 * Follows Single Responsibility Principle - only handles container creation
 *
 * @returns {Function} A function that returns or creates the container element
 */
export const useModalContainer = () => () => {
  let container = document.querySelector(
    '#RouteMonitor #Maps #adjustsLayoutContainer #shortStopDetailsModalWrapper'
  );

  if (!container) {
    container = document.createElement('div');
    container.id = 'shortStopDetailsModalWrapper';

    document
      .querySelector('#RouteMonitor #Maps #adjustsLayoutContainer')
      .appendChild(container);
  }

  return container;
};
